<template>
  <div>
    <div class="editor">
      <editor-menu-bubble class="menububble" :editor="editor" @hide="hideLinkMenu">
        <div
          slot-scope="{ commands, isActive, getMarkAttrs, menu }"
          class="menububble"
          :class="{ 'is-Active': menu.isActive && !isActive.figure() }"
          :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
        >
          <button
            class="menububble__Button"
            :class="{ 'is-Active': getMarkAttrs('align').textAlign === 'left' }"
            @click="commands.align({ textAlign: 'left' })"
          >
            <icon name="align-left" />
          </button>

          <button
            class="menububble__Button"
            :class="{ 'is-Active': getMarkAttrs('align').textAlign === 'center' }"
            @click="commands.align({ textAlign: 'center' })"
          >
            <icon name="align-center" />
          </button>

          <button
            class="menububble__Button"
            :class="{ 'is-Active': getMarkAttrs('align').textAlign === 'right' }"
            @click="commands.align({ textAlign: 'right' })"
          >
            <icon name="align-right" />
          </button>
          <form
            class="menububble__Form"
            v-if="linkMenuIsActive"
            @submit.prevent="setLinkUrl(commands.link, linkUrl, newWindow)"
          >
            <input
              class="menububble__Input"
              type="text"
              v-model="linkUrl"
              placeholder="https://"
              ref="linkInput"
              @keydown.esc="hideLinkMenu"
            />
            <div class="menububble__Checkbox">
              <input type="checkbox" name="newWindow" id="newWindow" v-model="newWindow" />
              <label for="newWindow">New tab</label>
            </div>
            <button class="menububble__Button" @click="setLinkUrl(commands.link, linkUrl, newWindow)" type="button">
              <span>Confirm</span>
              <icon name="check" weight="regular" />
            </button>
          </form>

          <template v-else>
            <button
              class="menububble__Button"
              @click="showLinkMenu(getMarkAttrs('link'))"
              :class="{ 'is-Active': isActive.link() }"
            >
              <span>{{ isActive.link() ? 'Update Link' : 'Add Link' }}</span>
              <icon name="link" weight="regular" />
            </button>
            <button
              v-if="isActive.link()"
              class="menububble__Button"
              @click="setLinkUrl(commands.link, '')"
              :class="{ 'is-Active': isActive.link() }"
            >
              <span>Remove link</span>
              <icon name="unlink" weight="regular" />
            </button>
          </template>
        </div>
      </editor-menu-bubble>

      <editor-menu-bar :editor="editor">
        <div class="menubar" slot-scope="{ commands, isActive }">
          <div class="menubar__Buttons">
            <button class="menubar__Button" :class="{ 'is-Active': isActive.bold() }" @click="commands.bold">
              <icon weight="regular" name="bold" />
            </button>

            <button class="menubar__Button" :class="{ 'is-Active': isActive.italic() }" @click="commands.italic">
              <icon weight="regular" name="italic" />
            </button>

            <button class="menubar__Button" :class="{ 'is-Active': isActive.strike() }" @click="commands.strike">
              <icon weight="regular" name="strikethrough" />
            </button>

            <button class="menubar__Button" :class="{ 'is-Active': isActive.underline() }" @click="commands.underline">
              <icon weight="regular" name="underline" />
            </button>

            <button title="Upload image" class="menubar__Button" @click="openImageModal(commands.figure)">
              <icon name="image" />
            </button>

            <button title="Insert PDF from URL" class="menubar__Button" @click="showPDFprompt(commands.pdfEmbed)">
              <icon name="file-pdf" />
            </button>

            <button title="Insert Bookmark" class="menubar__Button" @click="showBookmarkPrompt(commands.bookmarkEmbed)">
              <icon name="globe" />
            </button>

            <button title="Insert video from URL" class="menubar__Button" @click="showVideoPrompt(commands.videoEmbed)">
              <icon name="video" />
            </button>

            <button title="Insert Iframe" class="menubar__Button" @click="showCodePrompt(commands.codeEmbed)">
              <icon name="file" />
            </button>

            <button
              class="menubar__Button"
              :class="{ 'is-Active': isActive.bullet_list() }"
              @click="commands.bullet_list"
            >
              <icon weight="regular" name="list" />
            </button>

            <button
              class="menubar__Button"
              :class="{ 'is-Active': isActive.ordered_list() }"
              @click="commands.ordered_list"
            >
              <icon weight="regular" name="list-ol" />
            </button>

            <button class="menubar__Button" @click="commands.undo">
              <icon weight="regular" name="undo" />
            </button>

            <button class="menubar__Button" @click="commands.redo">
              <icon weight="regular" name="redo" />
            </button>

            <button
              class="menubar__Button"
              @click="commands.createTable({ rowsCount: 3, colsCount: 3, withHeaderRow: false })"
            >
              <icon name="table" />
            </button>

            <button class="menubar__Button" @click="commands.horizontal_rule">
              <icon weight="regular" name="horizontal-rule" />
            </button>

            <div class="menubar__Dropdown">
              <p class="menubar__DropdownTitle" :class="{ 'is-Active': isActive.heading() }">
                Heading styles
                <icon name="chevron-down" weight="regular" />
              </p>
              <div class="menubar__DropdownMenu">
                <button
                  v-for="(level, index) in styles.heading"
                  class="menubar__Button"
                  :class="{ 'is-Active': isActive.heading({ level }) }"
                  :style="{ fontSize: `${30 - level / 0.4}px` }"
                  @click="commands.heading({ level })"
                  :key="`heading-${index}`"
                >
                  Heading {{ level }}
                  <icon name="eye" weight="regular" v-if="isActive.heading({ level })" />
                </button>
              </div>
            </div>

            <div class="menubar__Dropdown">
              <p class="menubar__DropdownTitle" :class="{ 'is-Active': isActive.quote() }">
                Quote styles
                <icon name="chevron-down" weight="regular" />
              </p>
              <div class="menubar__DropdownMenu">
                <button
                  v-for="(style, index) in styles.quote"
                  class="menubar__Button"
                  :class="{ 'is-Active': isActive.quote({ style: style.value }) }"
                  @click="commands.quote({ style: style.value })"
                  :key="`quote-${index}`"
                >
                  {{ style.name }}
                  <icon name="eye" weight="regular" v-if="isActive.quote({ style: style.value })" />
                </button>
              </div>
            </div>
          </div>

          <div class="menubar__Tables" v-if="isActive.table()">
            <button class="menubar__Button" @click="commands.deleteTable">
              <icon name="delete_table" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.addColumnBefore">
              <icon name="add_col_before" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.addColumnAfter">
              <icon name="add_col_after" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.deleteColumn">
              <icon name="delete_col" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.addRowBefore">
              <icon name="add_row_before" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.addRowAfter">
              <icon name="add_row_after" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.deleteRow">
              <icon name="delete_row" weight="toolbar" />
            </button>
            <button class="menubar__Button" @click="commands.toggleCellMerge">
              <icon name="combine_cells" weight="toolbar" />
            </button>
          </div>
        </div>
      </editor-menu-bar>
      <editor-content class="editor__Content" :editor="editor" />
      <p v-if="base64" class="formLabel formLabel--Base64">
        You have uploaded base64 image format. Please use image uploader from the menu above.
      </p>
    </div>
    <image-modal ref="imageModal" @onConfirm="addImage" />
  </div>
</template>

<script>
// import { codemirror } from 'vue-codemirror'
// import 'codemirror/mode/xml/xml.js'

import { HTTP } from '@/http-common.js'
import ImageModal from '@/components/editor/tiptap/ImageModal.vue'
import Align from '@/components/editor/tiptap/Align.js'
import { Editor, EditorContent, EditorMenuBubble, EditorMenuBar } from 'tiptap'
import {
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  Strike,
  Underline,
  HardBreak,
  Heading,
  Image,
  Bold,
  Italic,
  History,
  TrailingNode,
  Table,
  TableHeader,
  TableCell,
  TableRow,
} from 'tiptap-extensions'
import Figure from '@/components/editor/tiptap/Figure.js'
import PDFEmbed from '@/components/editor/tiptap/PDFEmbed.js'
import BookmarkEmbed from '@/components/editor/tiptap/BookmarkEmbed.js'
import VideoEmbed from '@/components/editor/tiptap/VideoEmbed.js'
import CustomLink from '@/components/editor/tiptap/CustomLink.js'
import CodeEmbed from '@/components/editor/tiptap/CodeEmbed.js'
import { QuoteStyles, Quote } from '@/components/editor/tiptap/Quote.js'
export default {
  components: {
    ImageModal,
    EditorMenuBubble,
    EditorMenuBar,
    EditorContent,
    // codemirror
  },
  props: ['value'],
  data() {
    return {
      base64: false,
      editor: new Editor({
        extensions: [
          new BulletList(),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new Strike(),
          new Underline(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3, 4, 5, 6] }),
          new Image(),
          new Bold(),
          new Italic(),
          new History(),
          new TrailingNode(),
          new Table({
            resizable: true,
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
          // custom extensions
          new CustomLink(),
          new Figure(),
          new PDFEmbed(),
          new BookmarkEmbed(),
          new VideoEmbed(),
          new CodeEmbed(),
          new Quote(),
          new Align(),
        ],
        onDrop() {
          // Disable drag and drop of images
          return true
        },
        onUpdate: ({ getJSON, getHTML }) => {
          this.html = getHTML()
          this.json = getJSON()

          // Remove empty paragraph upon save
          if (this.html === '<p></p>') this.html = ''
          this.$emit('input', this.html)
        },
        onFocus: () => {
          this.editorFocus = true
        },
        onBlur: () => {
          this.editorFocus = false
        },
        content: this.value,
      }),
      editorHTML: false,
      editorFocus: false,
      html: '',
      json: null,
      linkUrl: null,
      newWindow: false,
      linkMenuIsActive: false,
      showImageModal: false,
      image: null,
      styles: {
        heading: [1, 2, 3, 4, 5, 6],
        quote: QuoteStyles,
      },
    }
  },
  watch: {
    value(val) {
      if (val && this.editor && this.editorFocus === false) {
        this.editor.setContent(val, true)
      }
      // if (val.includes('base64')) {
      //   this.base64 = true
      //   this.highlightBase64()
      // } else {
      //   this.base64 = false
      // }
    },
  },
  mounted() {
    this.editor.setContent(this.value, true)
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    highlightBase64() {
      const images = document.getElementsByTagName('img')
      Array.from(images).forEach((image) => {
        const imgSrc = image.getAttribute('src')
        if (imgSrc && imgSrc.includes('base64')) {
          image.classList.add('base64Image')
        }
      })
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url, newWindow) {
      command({ href: url, target: newWindow ? '_blank' : '_self' })
      this.hideLinkMenu()
    },
    openImageModal(command) {
      this.$refs.imageModal.showModal(command)
    },
    addImage(data) {
      if (data.command !== null) data.command(data.data)
    },
    getVideoEmbedURL(url) {
      const vimeo = /(?:http?s?:\/\/)?(?:www\.)?(?:vimeo\.com)\/?(.+)/g
      const youtube = /(?:http?s?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?(.+)/g
      const facebook = /(.+)/g

      if (vimeo.test(url)) {
        const replacement = '//player.vimeo.com/video/$1'
        url = url.replace(vimeo, replacement)
      } else if (youtube.test(url)) {
        const replacement = '//www.youtube.com/embed/$1'
        url = url.replace(youtube, replacement)
      } else if (facebook.test(url)) {
        const replacement = '//www.facebook.com/plugins/video.php?href=$1&show_text=&width=570&height=300'
        url = url.replace(facebook, replacement)
      }

      return url
    },
    async getBookmark(url) {
      const { data } = await HTTP.post('scraper/og', { url })
      return data
    },
    getIframeURL(url) {
      const href = url.match(/src="(.*?)"/i)
      return href ? href[1] : url
    },
    showVideoPrompt(command) {
      const src = prompt('Enter the url of your youtube or vimeo video.')
      if (src !== null) {
        command({ src: this.getVideoEmbedURL(src) })
      }
    },
    showPDFprompt(command) {
      const src = prompt('Enter the URL of your PDF')
      if (src !== null) {
        command({ src })
      }
    },
    async showBookmarkPrompt(command) {
      const src = prompt('Enter the URL of your Website')
      if (src !== null) {
        const { title, description, thumbnail_url, external_url } = await this.getBookmark(src)
        command({ title, dataContent: description, src: thumbnail_url, href: external_url })
      }
    },
    showCodePrompt(command) {
      const src = prompt('Paste the URL of your embedded content (ideally the embed code along with the <iframe> tag)')
      if (src !== null) {
        command({ src: this.getIframeURL(src) })
      }
    },
  },
}
</script>
